// insight_rules.js — guards that keep Home's and Progress's advice honest in
// the first weeks and consistent with the active program. Pure functions.
//
//   trainedVolumeReport(state, now)   volumeReport, but the "4-week average" is
//                                     taken over the complete weeks actually
//                                     trained (a 2-week-old user isn't "low"
//                                     because weeks before they started count as 0)
//   programWeeklySets(state, program) planned hard sets per muscle per rotation
//   plannedDeload(state, program, now) the program's own upcoming deload, if any
//   prsThisWeek(state, now)           real PRs this week (first-ever logs are baselines)

import { completedSessions, indexExercises, muscleSets, musclesFor, volumeReport, weekStart, VOLUME_LANDMARKS } from './analytics.js';
import { cycleOf, nextWorkout } from './progression.js';

const DAY = 86400000;
const WEEK = 7 * DAY;

export function trainedVolumeReport(state, now = Date.now()) {
  const rep = volumeReport(state, now);
  const done = completedSessions(state);
  const ws = weekStart(now);
  const first = done.length ? weekStart(done[0].startedAt) : ws;
  // Complete weeks since the first workout (the current week is never complete).
  const weeksTrained = Math.max(0, Math.min(4, Math.round((ws - first) / WEEK)));
  if (weeksTrained >= 4) return rep.map(r => ({ ...r, weeksTrained }));
  const idx = indexExercises(state);
  const sums = {};
  for (let k = 1; k <= weeksTrained; k++) {
    const m = muscleSets(state, ws - k * WEEK, ws - (k - 1) * WEEK, idx);
    for (const [mu, v] of Object.entries(m)) sums[mu] = (sums[mu] || 0) + v;
  }
  return rep.map(r => {
    const avg4 = weeksTrained ? +((sums[r.muscle] || 0) / weeksTrained).toFixed(1) : 0;
    const lm = VOLUME_LANDMARKS[r.muscle];
    let status = 'ok';
    if (!weeksTrained) status = 'new';
    else if (avg4 < lm.mev && lm.mev > 0) status = 'low';
    else if (avg4 > lm.mrv) status = 'high';
    return { ...r, avg4, status, weeksTrained };
  });
}

// Planned hard sets per muscle for one rotation of the program's days.
export function programWeeklySets(state, program) {
  const out = {};
  if (!program) return out;
  const idx = indexExercises(state);
  for (const d of program.days || []) {
    for (const pe of d.exercises || []) {
      const ex = idx[pe.exerciseId] || { group: pe.group };
      const n = Math.max(0, +pe.sets || 0);
      const { primary, secondary } = musclesFor(ex);
      primary.forEach(m => { out[m] = (out[m] || 0) + n; });
      secondary.forEach(m => { out[m] = (out[m] || 0) + n * 0.5; });
    }
  }
  return out;
}

// The active program's own deload: { inWeeks (0 = this week), label } within `horizon` weeks, else null.
export function plannedDeload(state, program, now = Date.now(), horizon = 2) {
  if (!program) return null;
  const { weeks, repeat } = cycleOf(program);
  if (!weeks.some(w => w.deload)) {
    return program.deloadWeek && program.deloadWeek === weekStart(now) ? { inWeeks: 0, label: 'Deload week' } : null;
  }
  const nx = nextWorkout(state, program.id, now);
  const n = weeks.length;
  for (let k = 0; k <= horizon; k++) {
    const i = nx.week + k;
    if (!repeat && i >= n) break;
    const w = weeks[i % n];
    if (w && w.deload) return { inWeeks: k, label: w.label || `Week ${(i % n) + 1}`, weekNumber: (i % n) + 1 };
  }
  return null;
}

// PRs this week that beat an earlier best (not first-ever logs).
export function prsThisWeek(state, now = Date.now()) {
  const ws = weekStart(now);
  const firstSeen = {};
  for (const s of completedSessions(state)) {
    for (const st of s.sets || []) if (st.exerciseId && firstSeen[st.exerciseId] == null) firstSeen[st.exerciseId] = s.startedAt;
  }
  return (state.prs || []).filter(p => {
    const t = new Date(p.date + 'T12:00:00').getTime();
    if (!(t >= ws)) return false;
    const f = firstSeen[p.exerciseId];
    return f != null && f < ws && weekStart(f) < ws;
  }).length;
}
